// page-backlog-review-list.ts — La liste des recommandations : filtres, tri,
// cartes, et la bascule vers la vue Tableau.
//
// Extrait de `page-backlog-review.ts` le 2026-09-13. Tout est PUR : chaque
// fonction rend du HTML à partir de la revue et d'un état explicite
// (`ListState`, possédé par l'écran). Les gestes ne sont pas ici — un bouton
// porte un `data-act`, l'écran le câble.
//
// La règle de cet écran vaut pour cette liste : **l'ordre ne change jamais
// après une action.** La ligne traitée change d'aspect SUR PLACE.
//
// Globaux ambiants : esc, t (core-globals.d.ts).

import {
  capForDisplay,
  filterRecommendations,
  pileCounts,
  sortRecommendations,
  type SortKey,
} from '../domain/backlog-review.js';
import { renderBoard, type BoardHandlers } from './page-backlog-review-board.js';
import { fmtDay } from './page-backlog-reviews.js';
import { label, type Reco, type Review } from './page-backlog-review-types.js';

/** Ce que l'écran possède et que la liste lit : les filtres vivent dans l'URL,
 *  le tri, le plafond levé et la vue sont des conforts de rendu. */
export interface ListState {
  pile: string;
  family: string;
  onlyFlagged: boolean;
  sort: SortKey;
  showAll: boolean;
  // Vue Liste par défaut. Le choix ne vit pas dans l'URL : c'est un confort de
  // rendu, pas un contexte à partager — contrairement au filtre courant.
  view: 'list' | 'board';
}

// Plafond d'AFFICHAGE des recommandations non traitées — jamais de détection.
// Tout ce qui a été trouvé est en base : « tout afficher » est instantané et
// gratuit, et le pied de liste dit toujours combien il en reste.
export const DISPLAY_CAP = 25;

export const PILES = ['todo', 'applied', 'rejected', 'snoozed'] as const;
const FAMILIES = [
  'fusion',
  'abandon',
  'cloture',
  'priorite',
  'raci',
  'creation',
  'vigilance',
  'mutualisation',
];

export function visibleRecos(recos: Reco[], state: ListState): Reco[] {
  return sortRecommendations(
    filterRecommendations(recos, {
      pile: state.pile,
      family: state.family,
      onlyFlagged: state.onlyFlagged,
    }),
    state.sort,
  );
}

const SORTS: readonly SortKey[] = ['reco', 'id'];

function _sortSelect(sort: SortKey): string {
  const options = SORTS.map(
    (k) =>
      `<option value="${k}"${sort === k ? ' selected' : ''}>${esc(t('review.sort.' + k))}</option>`,
  ).join('');
  return `<label class="flex gap-2" style="align-items:center">
      <span class="text-xs text-muted">${esc(t('review.sort.label'))}</span>
      <select id="reco-sort" style="font-size:var(--fs-sm)">${options}</select>
    </label>`;
}

function _pileChips(recos: Reco[], state: ListState): string {
  const counts = pileCounts(recos, PILES);
  const chips = PILES.map((p) => {
    const n = counts[p] ?? 0;
    const active = state.pile === p ? ' active' : '';
    return `<button class="tag-chip${active}" data-pile="${p}" aria-pressed="${state.pile === p}">${esc(label('review.pile', p))} ${n}</button>`;
  });
  const flagged = recos.filter((r) => r.to_arbitrate).length;
  chips.push(
    `<button class="tag-chip${state.onlyFlagged ? ' active' : ''}" data-flagged="1" aria-pressed="${state.onlyFlagged}">⚑ ${esc(t('review.chip.toArbitrate'))} ${flagged}</button>`,
  );
  return `<div class="flex gap-2" aria-live="polite" style="flex-wrap:wrap;margin-bottom:8px">${chips.join('')}</div>`;
}

function _familyChips(recos: Reco[], family: string): string {
  const chips = [
    `<button class="tag-chip${family ? '' : ' active'}" data-family="" aria-pressed="${!family}">${esc(t('review.chip.allFamilies'))}</button>`,
  ];
  for (const f of FAMILIES) {
    const n = recos.filter((r) => r.family === f).length;
    if (!n) continue;
    chips.push(
      `<button class="tag-chip${family === f ? ' active' : ''}" data-family="${f}" aria-pressed="${family === f}">${esc(label('review.family', f))} ${n}</button>`,
    );
  }
  return `<div class="flex gap-2" style="flex-wrap:wrap;margin-bottom:12px">${chips.join('')}</div>`;
}

export function targetsHtml(reco: Reco): string {
  const actions = reco.targets.filter((tg) => tg.type === 'action');
  if (!actions.length) return '';
  const underline = reco.changed_since_analysis
    ? ' style="text-decoration:underline dotted" title="' + esc(changesTooltip(reco)) + '"'
    : '';
  // `#62`, la notation des actions dans le reste de l'app — pas `A-62`, qui
  // n'existait que dans la revue.
  return actions.map((tg) => `<span${underline}>#${tg.id}</span>`).join(' + ') + ' ·';
}

export function changesTooltip(reco: Reco): string {
  const lines = (reco.changes || []).map(
    (c) => `${c.label} : ${String(c.before ?? '—')} → ${String(c.after ?? '—')}`,
  );
  const who = (reco.changes || []).find((c) => c.by_trigram);
  if (who) lines.push(t('review.changes.by', { who: who.by_trigram || '', date: who.at || '' }));
  return lines.join('\n');
}

/** L'écran métier qui porte l'écriture, par famille.
 *
 * « J'applique » n'écrit JAMAIS depuis la revue : il ouvre l'écran métier
 * existant, qui a ses propres permissions, son audit et son versionnage. Une
 * `vigilance` n'en a aucun — c'est un trou signalé, il se traite par une
 * création ou se rejette — donc elle n'offre pas le geste.
 */
export function businessScreen(reco: Reco): string | null {
  const actions = reco.targets.filter((tg) => tg.type === 'action');
  if (reco.family === 'vigilance') return null;
  if (reco.family === 'creation') return '#actions';
  if (reco.family === 'fusion' && actions.length === 2) {
    return `#actions/merge/${actions[0]?.id}/${actions[1]?.id}`;
  }
  return actions.length ? `#action/${actions[0]?.id}` : null;
}

function _actionsBar(reco: Reco): string {
  if (reco.pile === 'applied') return '';
  const disabled = reco.unavailable
    ? ' disabled title="' + esc(t('review.unavailable.why')) + '"'
    : '';
  const buttons = [];
  if (businessScreen(reco)) {
    buttons.push(
      `<button class="btn btn-primary btn-sm" data-act="applied" data-id="${reco.id}"${disabled}>${esc(t('review.act.apply'))}</button>`,
    );
  }
  buttons.push(
    `<button class="btn btn-secondary btn-sm" data-act="rejected" data-id="${reco.id}">${esc(t('review.act.reject'))}</button>`,
    `<button class="btn btn-secondary btn-sm" data-act="snoozed" data-id="${reco.id}">${esc(t('review.act.later'))}</button>`,
  );
  if (reco.pile !== 'todo') {
    buttons.push(
      `<button class="btn btn-neutral btn-sm" data-act="todo" data-id="${reco.id}">${esc(t('review.act.resume'))}</button>`,
    );
  }
  return `<div class="flex gap-2" style="flex-wrap:wrap;margin-top:8px">${buttons.join('')}</div>`;
}

interface ActionProfile {
  vitality?: string | null;
}

/** La vitalité figée de CHAQUE action visée, dans l'ordre des cibles.
 *
 * Toutes, et non la première seule : une fusion vise deux actions qui peuvent
 * avoir deux vitalités, et n'en montrer qu'une cachait la moitié de ce sur quoi
 * on demande de décider. Aucune moyenne non plus — le constat n'en a jamais
 * mesuré.
 */
export function vitalitiesOf(
  review: Review | null,
  reco: Pick<Reco, 'targets'>,
): Array<{ id: number; vitality: string }> {
  if (!review) return [];
  const profiles = (review.constat?.['action_profiles'] || {}) as Record<string, ActionProfile>;
  const out: Array<{ id: number; vitality: string }> = [];
  for (const target of reco.targets || []) {
    if (target.type !== 'action') continue;
    const vitality = profiles[String(target.id)]?.vitality;
    if (vitality) out.push({ id: target.id, vitality });
  }
  return out;
}

/** Les pastilles de vitalité des actions visées.
 *
 * La priorité a été RETIRÉE le 2026-08-26 : deux notions de priorité à quelques
 * centimètres — celle du périmètre dans le constat, celle de l'action sur la
 * carte — se lisaient comme une seule et se contredisaient.
 *
 * Le numéro d'action précède la vitalité dès qu'il y en a plusieurs : sans lui,
 * deux pastilles côte à côte sur une fusion ne disent pas laquelle est laquelle.
 */
export function profileBadges(items: Array<{ id: number; vitality: string }>): string {
  if (!items.length) return '';
  const withRef = items.length > 1;
  return items
    .map(
      (item) =>
        `<span class="badge badge-outline" title="${esc(t('review.constat.vitality'))}">${
          withRef ? `#${item.id} ` : ''
        }${esc(label('review.vitality', item.vitality))}</span>`,
    )
    .join(' ');
}

export function recoBlock(review: Review, reco: Reco): string {
  const dimStyle = reco.pile === 'todo' ? '' : 'opacity:.65;';
  const banner = reco.unavailable
    ? `<div class="badge badge-warning">${esc(t('review.unavailable.badge'))}</div>`
    : '';
  const stale = reco.no_longer_detected_at
    ? `<div class="text-xs text-muted">${esc(t('review.noLongerDetected'))}</div>`
    : '';
  const resurfaced = reco.resurfaced_reason
    ? `<div class="text-xs text-muted">${esc(reco.resurfaced_reason)}</div>`
    : '';
  const annotation = reco.annotation
    ? `<div class="text-xs" style="margin-top:4px">✎ ${esc(reco.annotation)}</div>`
    : '';
  // Le retour de comité porte sur la POSITION proposée : « validée » sur une
  // proposition de rejet confirme le rejet. Le libellé le dit donc en toutes
  // lettres plutôt que d'afficher un verdict nu.
  const committee = reco.committee_outcome
    ? `<div class="text-xs" style="margin-top:4px;font-weight:600">${esc(
        t('review.committee.mention.' + reco.committee_outcome, {
          date: fmtDay(reco.committee_date),
        }),
      )}${reco.committee_comment ? ' — ' + esc(reco.committee_comment) : ''}</div>`
    : '';
  return `<div class="card" style="${dimStyle}padding:12px;margin-bottom:8px" data-reco="${reco.id}">
      <div class="flex gap-2" style="align-items:baseline;flex-wrap:wrap">
        <span class="badge">${esc(label('review.family', reco.family))}</span>
        ${reco.is_new ? `<span class="badge badge-active">${esc(t('review.new.badge'))}</span>` : ''}
        ${reco.to_arbitrate ? `<span class="badge badge-warning">⚑ ${esc(t('review.chip.toArbitrate'))}</span>` : ''}
        ${profileBadges(vitalitiesOf(review, reco))}
        <span class="text-mono text-xs">${targetsHtml(reco)}</span>
        <strong>${esc(reco.title)}</strong>
        <span style="margin-left:auto" class="flex gap-2">
          <button class="btn btn-secondary btn-sm" data-flag="${reco.id}"
            aria-pressed="${reco.to_arbitrate}"
            aria-label="${esc(reco.to_arbitrate ? t('review.flag.remove') : t('review.flag.set'))}"
            title="${esc(reco.to_arbitrate ? t('review.flag.remove') : t('review.flag.set'))}">⚑</button>
          <button class="btn btn-secondary btn-sm" data-note="${reco.id}" style="color:#147A57"
            aria-label="${esc(t('review.annotate'))}" title="${esc(t('review.annotate'))}">✎</button>
        </span>
      </div>
      ${banner}
      <div style="margin:6px 0">${esc(reco.rationale)}</div>
      ${resurfaced}${stale}${committee}${annotation}
      <div class="text-xs text-muted">${esc(label('review.provenance', reco.provenance))} · ${esc(label('review.confidence', reco.confidence))}</div>
      ${_actionsBar(reco)}
    </div>`;
}

function _footer(shown: number, total: number): string {
  return `<div class="text-xs text-muted" style="margin-top:8px">
      ${esc(t('review.cap.footer', { shown: String(shown), total: String(total) }))}
      <button class="btn btn-secondary btn-sm" id="btn-show-all">${esc(t('review.cap.showAll'))}</button>
    </div>`;
}

function _viewSwitch(current: 'list' | 'board'): string {
  const button = (view: 'list' | 'board', icon: string, text: string): string =>
    `<button class="tag-chip${current === view ? ' active' : ''}" data-view="${view}"
      aria-pressed="${current === view}" title="${esc(text)}">
      <svg class="icon"><use href="/static/svg/icons.svg#${icon}"></use></svg> ${esc(text)}
    </button>`;
  // `mobile-hide-when-ro` : sur mobile l'app est consultative, et il n'y a pas
  // de glisser-déposer tactile ici. La vue Liste reste accessible, elle.
  return `<div class="flex gap-2 mobile-hide-when-ro">
      ${button('list', 'icon-list', t('review.view.list'))}
      ${button('board', 'icon-stack', t('review.view.board'))}
    </div>`;
}

/** La carte « Recommandations » entière : entête, filtres, cartes ou tableau. */
export function renderRecos(review: Review, state: ListState, handlers: BoardHandlers): string {
  const all = review.recommendations || [];
  // Le plafond porte sur les NON TRAITÉES : à mesure que la pile se vide, les
  // suivantes entrent d'elles-mêmes, sans qu'il soit besoin d'itérer.
  // Tri et bascule de vue forment UN groupe, poussé à droite d'un bloc. Collés
  // au titre, « Recommandations » et « Trier par » se lisaient comme une seule
  // phrase.
  const header = `<div class="card-header">
      <div class="card-title">${esc(t('review.recos.title'))}</div>
      <div class="flex gap-4" style="margin-left:auto;align-items:center">
        ${_sortSelect(state.sort)}
        ${_viewSwitch(state.view)}
      </div>
    </div>`;
  if (state.view === 'board') {
    // La vue Tableau montre les QUATRE piles à la fois : le filtre de pile n'y
    // a pas de sens, seuls la famille et le drapeau restent utiles.
    const rows = sortRecommendations(
      filterRecommendations(all, { family: state.family, onlyFlagged: state.onlyFlagged }),
      state.sort,
    );
    return `<div class="card">${header}${_familyChips(all, state.family)}${renderBoard(rows, handlers)}</div>`;
  }
  const view = capForDisplay(visibleRecos(all, state), DISPLAY_CAP, {
    pile: state.pile,
    showAll: state.showAll,
  });
  const body = view.shown.length
    ? view.shown.map((reco) => recoBlock(review, reco)).join('')
    : `<div class="empty">${esc(t('review.list.emptyPile'))}</div>`;
  return `<div class="card">
      ${header}
      ${_pileChips(all, state)}
      ${_familyChips(all, state.family)}
      ${body}
      ${view.capped ? _footer(view.shown.length, view.total) : ''}
    </div>`;
}
